/**
 * Hilfsfunktionen für `SimpleTable`: Feldzugriff, Textdarstellung, Suche und
 * Sortierung. Bewusst ohne React, damit sie einzeln prüfbar bleiben.
 */

import type { SortDirection } from '@/api/gridTypes';
import { formatDate } from '@/utils/date';

/**
 * Liest ein Feld anhand seines Namens. Die Zeilentypen der Anwendung haben
 * keinen Indexsignatur-Typ; der Zugriff erfolgt daher wie im Altclient
 * (`GridColumn.Field`) dynamisch.
 */
export function getFieldValue<T>(row: T, field: string): unknown {
  return (row as unknown as Record<string, unknown>)[field];
}

/** Erzeugt eine Kopie der Zeile mit geändertem Feld. */
export function withField<T>(row: T, field: string, value: unknown): T {
  return { ...row, [field]: value };
}

/** Anzeigetext eines Rohwerts; unbekannte Strukturen ergeben einen leeren Text. */
export function toText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  if (value instanceof Date) return formatDate(value);
  return '';
}

/** Sortierreihenfolge zweier Rohwerte; leere Werte stehen hinten. */
export function compareValues(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;

  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();

  return toText(a).localeCompare(toText(b), 'de-DE');
}

/**
 * Wendet Suchtext und Sortierung an — beides clientseitig, wie bei einer
 * `SfGrid` mit `DataSource`. Die Suche prüft alle Felder, auch unsichtbare.
 */
export function filterAndSortRows<T>(
  rows: T[],
  fields: string[],
  search: string,
  sort: { field: string; direction: SortDirection } | null,
): T[] {
  const needle = search.trim().toLowerCase();
  const filtered =
    needle === ''
      ? rows
      : rows.filter((row) =>
          fields.some((field) => toText(getFieldValue(row, field)).toLowerCase().includes(needle)),
        );
  if (sort === null) return filtered;

  const factor = sort.direction === 'descending' ? -1 : 1;
  return [...filtered].sort(
    (a, b) => factor * compareValues(getFieldValue(a, sort.field), getFieldValue(b, sort.field)),
  );
}
